import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import React, { useContext, useEffect, useState } from "react";

import { ContentCopy, Edit } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import {
  Button,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import { Stack } from "@mui/system";

import { PublisherType } from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/v1/publisher_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  createPublisher,
  getPublisher,
  updatePublisher,
} from "api/endpoints/content-library";
import { DisplayToastAlert } from "components/shared/display-toast-alert";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

interface Props {
  roleId: string;
  orgName: string;
}

const slugRegex = /[^a-z0-9\-]+/g;

const PUBLISHER_BASE_URL = "https://safetyculture.com/library/publishers";

interface SlugWarningDialogProps {
  open: boolean;
  oldSlug: string;
  newSlug: string;
  acknowledged: boolean;
  onAcknowledgeChange: (checked: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

const SlugWarningDialog = ({
  open,
  oldSlug,
  newSlug,
  acknowledged,
  onAcknowledgeChange,
  onCancel,
  onConfirm,
  isPending,
}: SlugWarningDialogProps) => {
  const { copyToClipboard } = useContext(SnackbarContext);

  // The paths are basePath-relative: the frontend-content-library repo sets
  // basePath "/library" and applies permanent:true automatically, so entries
  // must NOT include the /library prefix.
  const redirectSnippet = JSON.stringify(
    { source: `/publishers/${oldSlug}`, destination: `/publishers/${newSlug}` },
    null,
    2,
  );

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>⚠️ Changing publisher slug breaks existing URLs</DialogTitle>
      <DialogContent>
        <Stack direction="column" gap="1rem">
          <Typography variant="body1" color="error">
            Changing the slug moves the publisher&apos;s public page. There is{" "}
            <strong>no automatic redirect</strong> — if a redirect is not added
            manually, the old URL will 404 and SEO ranking will be lost.
          </Typography>
          <Typography variant="body2">
            <strong>Old URL:</strong>{" "}
            <code>
              {PUBLISHER_BASE_URL}/{oldSlug}
            </code>
          </Typography>
          <Typography variant="body2">
            <strong>New URL:</strong>{" "}
            <code>
              {PUBLISHER_BASE_URL}/{newSlug}
            </code>
          </Typography>
          <Typography variant="body2">
            Add the following entry to{" "}
            <code>
              frontend-content-library/apps/content-library/redirects.json
            </code>
            :
          </Typography>
          <Stack direction="row" alignItems="flex-start" gap="0.5rem">
            <Typography
              component="pre"
              variant="body2"
              sx={{ fontFamily: "monospace", flexGrow: 1, whiteSpace: "pre" }}
            >
              {redirectSnippet}
            </Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton
                size="small"
                onClick={() => copyToClipboard(redirectSnippet)}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => onAcknowledgeChange(e.target.checked)}
              />
            }
            label="I understand a redirect must be added in frontend-content-library"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!acknowledged}
          loading={isPending}
          onClick={onConfirm}
        >
          Update publisher
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OrgContentLibraryTab = (props: Props) => {
  const orgUUID = toUUID(props.roleId);

  const [isPublisherDataError, setIsPublisherDataError] = useState(false);
  const [isPublisher, setIsPublisher] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditLocked, setIsEditLocked] = useState(true);
  const [isSlugWarningOpen, setIsSlugWarningOpen] = useState(false);
  const [isSlugChangeAcknowledged, setIsSlugChangeAcknowledged] =
    useState(false);
  const {
    data: publisherResponse,
    isLoading: isLoadingPublisher,
    error: publisherError,
  } = useQuery({
    queryKey: ["getPublisher", orgUUID],
    queryFn: () => getPublisher(orgUUID),
  });

  const queryClient = useQueryClient();
  const createMutation = useMutation({ mutationFn: createPublisher });
  const updateMutation = useMutation({ mutationFn: updatePublisher });

  const autoSlug = React.useMemo(() => {
    if (!props.orgName) {
      return "";
    }
    return props.orgName.toLowerCase().replace(slugRegex, "-");
  }, [props.orgName]);

  const [formState, setFormState] = useState({
    title: props.orgName || "",
    orgId: orgUUID,
    slug: autoSlug,
    publisherType: PublisherType.PARTNERED,
    enabled: true,
  });

  useEffect(() => {
    if (props.orgName && !formState.title) {
      setFormState((prev) => ({
        ...prev,
        title: props.orgName,
        slug: autoSlug,
      }));
    }
  }, [props.orgName, autoSlug, formState.title]);

  useEffect(() => {
    if (!publisherResponse) return;
    // Don't clobber in-progress edits; sync only when the form is locked.
    if (!isEditLocked) return;

    if (publisherResponse.publisher?.id) {
      setIsPublisher(true);
      setFormState({
        title: publisherResponse.publisher.title,
        orgId: publisherResponse.publisher.orgId,
        slug: publisherResponse.publisher.slug,
        publisherType: publisherResponse.publisher.publisherType,
        enabled: publisherResponse.publisher.enabled,
      });
    } else if (publisherResponse.publisher && !publisherResponse.publisher.id) {
      setIsPublisherDataError(true);
    }
  }, [publisherResponse, isEditLocked]);

  useEffect(() => {
    if (publisherError) {
      console.error("GetPublisher() returned error", publisherError);
    }
  }, [publisherError]);

  const isPartneredType = formState.publisherType === PublisherType.PARTNERED;

  function isSlugInvalid() {
    // Validate character set for any non-empty slug regardless of publisher type.
    if (formState.slug && formState.slug.match(slugRegex)) return true;
    // Slug is required only for partnered publishers.
    if (isPartneredType && !formState.slug) return true;
    return false;
  }

  function isCreateDisabled() {
    return !formState.title.trim() || isSlugInvalid() || isPublisherDataError;
  }

  const { hasPermissions, permissions } = useContext(PermissionsContext);

  if (permissions.isPermissionsLoading || isLoadingPublisher) {
    return (
      <CardContent>
        <CircularProgress />
      </CardContent>
    );
  }

  const canCreatePublisher = hasPermissions([ActionType.CREATE_PUBLISHER]);
  const canUpdatePublisher = hasPermissions([ActionType.UPDATE_PUBLISHER]);

  function handleCreate() {
    createMutation
      .mutateAsync({ ...formState, title: formState.title.trim() })
      .then(() => {
        setIsCreating(false);
        setIsPublisher(true);
      })
      .catch(() => {
        // This could be a serious scenario where the DB is out of sync with CT
        // we want this to be addressed properly instead of the user trying it again
        setIsPublisherDataError(true);
      })
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ["getPublisher", orgUUID] });
      });
  }

  function isUpdateDisabled() {
    return !canUpdatePublisher || !formState.title.trim() || isSlugInvalid();
  }

  function handleCancelEdit() {
    if (publisherResponse?.publisher) {
      setFormState({
        title: publisherResponse.publisher.title,
        orgId: publisherResponse.publisher.orgId,
        slug: publisherResponse.publisher.slug,
        publisherType: publisherResponse.publisher.publisherType,
        enabled: publisherResponse.publisher.enabled,
      });
    }
    setIsEditLocked(true);
  }

  function doUpdate() {
    updateMutation
      .mutateAsync({ ...formState, title: formState.title.trim() })
      .then(() => {
        setIsEditLocked(true);
        queryClient.invalidateQueries({ queryKey: ["getPublisher", orgUUID] });
      })
      .catch(() => {
        // Error is surfaced via updateMutation.isError toast
      });
  }

  function handleUpdate() {
    const oldSlug = publisherResponse?.publisher?.slug ?? "";
    // Only warn when both slugs are non-empty and actually differ; a first-time
    // slug assignment (empty → value) has nothing to redirect from.
    const slugChanged =
      !!oldSlug && !!formState.slug && formState.slug !== oldSlug;
    if (slugChanged) {
      setIsSlugChangeAcknowledged(false);
      setIsSlugWarningOpen(true);
      return;
    }
    doUpdate();
  }

  if (isPublisherDataError && !isCreating) {
    return (
      <CardContent>
        <Typography variant="body1" color="error">
          There is inconsistent data for {props.orgName}. Contact
          #the-librarians before making changes.
        </Typography>
      </CardContent>
    );
  }

  return (
    <>
      <Stack direction="column">
        <CardContent>
          <Typography variant="h6">
            Content Library Publisher Settings
          </Typography>
        </CardContent>

        {!isCreating && !isPublisher && (
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow
                  style={{
                    paddingBottom: "2rem",
                  }}
                >
                  <TableCell align="right" style={{ width: "50%" }}>
                    <Typography
                      variant="body1"
                      style={{ alignContent: "center" }}
                    >
                      {props.orgName} is not currently designated as a
                      publisher.
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Stack direction="row" alignItems="center">
                      <Button
                        variant="outlined"
                        disabled={!canCreatePublisher || isPublisherDataError}
                        onClick={() => {
                          setIsCreating(true);
                        }}
                      >
                        Enable as publisher
                      </Button>
                      {!canCreatePublisher && (
                        <Tooltip
                          title={"You need Create Publisher permission."}
                        >
                          <InfoIcon />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {isPublisher && (
          <CardContent>
            <Stack direction="row" justifyContent="center">
              {publisherResponse?.publisher?.enabled ? (
                <>
                  <Typography variant="body1">
                    {props.orgName} is designated as a publisher.
                  </Typography>
                  <CheckIcon color="success" />
                </>
              ) : (
                <Typography variant="body1">
                  {props.orgName} is not a publisher.
                </Typography>
              )}
            </Stack>
          </CardContent>
        )}
        {isCreating && (
          <CardContent style={{ borderTop: "2px double" }}>
            <Stack direction="column">
              <Typography variant="h6">Fill in publisher details</Typography>
              <Stack direction="row" gap="1rem" justifyContent="end">
                <Button
                  variant="contained"
                  disabled={!canCreatePublisher || isCreateDisabled()}
                  loading={createMutation.isPending}
                  onClick={handleCreate}
                >
                  Create publisher
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsCreating(false);
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        )}
        {isPublisher && (
          <CardContent style={{ borderTop: "2px double" }}>
            <Stack direction="column">
              <Typography variant="h6">Primary publisher details</Typography>
              <Stack direction="row" justifyContent="end" alignItems="center">
                {isEditLocked && (
                  <Button
                    startIcon={<Edit fontSize="small" />}
                    variant="outlined"
                    onClick={() => {
                      setIsEditLocked(false);
                    }}
                  >
                    Edit
                  </Button>
                )}
                {!isEditLocked && !canUpdatePublisher && (
                  <Tooltip title={"You need Update Publisher permission."}>
                    <InfoIcon />
                  </Tooltip>
                )}
                {!isEditLocked && (
                  <Stack direction="row" gap="1rem">
                    <Button variant="outlined" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      disabled={isUpdateDisabled()}
                      loading={updateMutation.isPending}
                      onClick={handleUpdate}
                    >
                      Update publisher
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </CardContent>
        )}
        {(isCreating || isPublisher) && (
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="right" style={{ width: "50%" }}>
                    <Typography variant="body1">Publisher name*</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <TextField
                      disabled={isPublisher && isEditLocked}
                      data-testid="name-input-field"
                      value={formState.title}
                      size="small"
                      onChange={(event) => {
                        setFormState({
                          ...formState,
                          title: event.target.value,
                        });
                      }}
                    />
                    {!formState.title.trim() && (
                      <Typography color="error">Name is required.</Typography>
                    )}
                  </TableCell>
                </TableRow>
                {(isPublisher || isPartneredType) && (
                  <TableRow>
                    <TableCell align="right">
                      <Typography variant="body1">Slug*</Typography>
                    </TableCell>
                    <TableCell align="left">
                      <TextField
                        disabled={isPublisher && isEditLocked}
                        data-testid="slug-input-field"
                        size="small"
                        value={formState.slug}
                        onChange={(event) => {
                          setFormState({
                            ...formState,
                            slug: event.target.value,
                          });
                        }}
                      />
                      {formState.slug && formState.slug.match(slugRegex) && (
                        <Typography color="error">
                          Only dashes &quot;-&quot; and alphanumerical
                          characters are allowed.
                        </Typography>
                      )}
                      {isPartneredType && !formState.slug && (
                        <Typography color="error">Slug is required.</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell align="right">
                    <Typography variant="body1">Publisher type*</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Stack direction="row" gap="1rem" alignItems="center">
                      <Select
                        disabled={isPublisher && isEditLocked}
                        onChange={(event) => {
                          const newType = Number(
                            event.target.value,
                          ) as PublisherType;
                          setFormState({
                            ...formState,
                            publisherType: newType,
                          });
                        }}
                        value={formState.publisherType}
                        size="small"
                      >
                        {!isCreating && (
                          <MenuItem
                            value={PublisherType.UNREGISTERED}
                            key="unregistered"
                          >
                            Unregistered
                          </MenuItem>
                        )}
                        {!isCreating && (
                          <MenuItem
                            value={PublisherType.CUSTOMER}
                            key="customer"
                          >
                            Customer
                          </MenuItem>
                        )}
                        <MenuItem
                          value={PublisherType.PARTNERED}
                          key="partnered"
                        >
                          Partnered
                        </MenuItem>
                        {!isCreating && (
                          <MenuItem
                            value={PublisherType.PROMOTED_CUSTOMER}
                            key="promoted-customer"
                          >
                            Promoted customer
                          </MenuItem>
                        )}
                      </Select>
                      <Typography variant="subtitle2">
                        Partnered customer publishers have a blue tick next to
                        their name.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="right">
                    <Typography variant="body1">Enabled*</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Select
                      disabled={isPublisher && isEditLocked}
                      onChange={(event) => {
                        setFormState({
                          ...formState,
                          enabled: event.target.value === "true",
                        });
                      }}
                      value={formState.enabled}
                      size="small"
                    >
                      <MenuItem value="true" key="true">
                        YES
                      </MenuItem>
                      <MenuItem value="false" key="false">
                        NO
                      </MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
      <DisplayToastAlert
        message="Publisher created!"
        open={createMutation.isSuccess}
        severity="success"
        onClose={() => createMutation.reset()}
      />
      <DisplayToastAlert
        message="Failed to create publisher. Contact #the-librarians as the system could be in an invalid state."
        open={createMutation.isError}
        severity="error"
        onClose={() => createMutation.reset()}
      />
      <DisplayToastAlert
        message="Publisher updated!"
        open={updateMutation.isSuccess}
        severity="success"
        onClose={() => updateMutation.reset()}
      />
      <DisplayToastAlert
        message={
          updateMutation.error instanceof Error
            ? updateMutation.error.message
            : "Failed to update publisher. Try again later."
        }
        open={updateMutation.isError}
        severity="error"
        onClose={() => updateMutation.reset()}
      />
      <SlugWarningDialog
        open={isSlugWarningOpen}
        oldSlug={publisherResponse?.publisher?.slug ?? ""}
        newSlug={formState.slug}
        acknowledged={isSlugChangeAcknowledged}
        onAcknowledgeChange={setIsSlugChangeAcknowledged}
        onCancel={() => setIsSlugWarningOpen(false)}
        onConfirm={() => {
          setIsSlugWarningOpen(false);
          // Guard against a stale-state submit: if the slug no longer differs
          // from the server value (e.g. a background refetch while the dialog
          // was open somehow affected state), abort silently.
          const oldSlug = publisherResponse?.publisher?.slug ?? "";
          if (!oldSlug || !formState.slug || formState.slug === oldSlug) return;
          doUpdate();
        }}
        isPending={updateMutation.isPending}
      />
    </>
  );
};

export default OrgContentLibraryTab;
